/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useAuth0} from '@auth0/auth0-react';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {FC, useEffect, useState} from 'react';
import {Navigate} from 'react-router-dom';
import type {FeatureFlagSet} from '../flags';
import LoadingPage from '../pages/LoadingPage';
import {getValidLandingRoute, ROUTES} from '../routes';
import {useAuth0Store} from '../stores/auth0Store';
import {useUserStore} from '../stores/userStore';

const AuthCallbackPage: FC = () => {
  const {error: auth0LoginError, logout} = useAuth0();
  const auth0ReturnTo = useAuth0Store((state) => state.returnTo);
  const flags = useFlags<FeatureFlagSet>();
  const permissionsMap = useUserStore((state) => state.permissionsMap);
  const userSet = useUserStore((state) => !!state.userId);
  const hasRunRedirectCallback = useAuth0Store((state) => state.hasRunRedirectCallback);

  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [redirectError, setRedirectError] = useState<string | null>(null);

  const flagsReady = Object.keys(flags).length > 0;
  const hasPermissions = Object.keys(permissionsMap).length > 0;

  useEffect(() => {
    const processAuth = () => {
      if (!hasPermissions) {
        localStorage.setItem(
          'TRUSST_AUTH_ERROR',
          'Sorry, you have been logged out because you are missing permissions or roles. Please try signing in again or contact your administrator.',
        );
        void logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
        return;
      }

      if (auth0ReturnTo) {
        setRedirectTo(auth0ReturnTo);
        return;
      }

      // Use flags to determine a valid landing route.
      const validLandingRoute = getValidLandingRoute(flags);
      const nextRoute = validLandingRoute || ROUTES.DEFAULT;
      setRedirectTo(nextRoute);
    };

    if (auth0LoginError) {
      setRedirectError(auth0LoginError?.message || 'Authentication failed');
      setRedirectTo(ROUTES.HOME);
      return;
    }

    if (hasRunRedirectCallback && !redirectTo && flagsReady && userSet) {
      processAuth();
    }
  }, [redirectTo, auth0LoginError, auth0ReturnTo, flags, flagsReady, userSet, hasRunRedirectCallback]);

  return (
    <>
      <LoadingPage />
      {redirectTo && <Navigate to={redirectTo} state={{error: redirectError}} replace />}
    </>
  );
};

export default AuthCallbackPage;
